import { FC, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MainLayout } from '../MainLayout';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { trpc } from 'src/trpc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'src/components/Search';
import { Loader } from 'src/components/Loader';
import { DriverSheet } from './DriverSheet';
import { DriverColumns } from './DriverColumns';
import { Button } from '@/components/ui/button';
import { useOrganization } from 'src/context/OrganizationContext';

export const DriverDashboard: FC = () => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState('');
  const [isDriverSheetOpen, setIsDriverSheetOpen] = useState(false);
  const [params, setParams] = useSearchParams({
    offset: '0',
    sortDirection: 'asc',
    sortKey: '',
    search: '',
  });
  const offset = parseInt(params.get('offset')!);
  const limit = 10;

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, isPending, isFetching } = trpc.admin.driver.getAllDrivers.useQuery(
    {
      limit,
      offset: offset,
      sortDirection: params.get('sortDirection') as 'asc' | 'desc',
      sortKey: params.get('sortKey')!,
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  useEffect(() => {
    if (sorting.length) {
      params.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      params.set('sortKey', sorting[0].id);
      params.set('offset', '0');
      setParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  useEffect(() => {
    params.set('search', search || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching && !data)) {
    return (
      <MainLayout>
        <Loader title="Getting drivers" />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-row justify-normal">
        <h1 className="flex-1 text-3xl font-bold">Drivers</h1>
        <div className="flex align">
          <Button onClick={() => setIsDriverSheetOpen(true)}>Create New Driver</Button>
        </div>
      </div>
      <Search
        value={search}
        onChange={(value) => setSearch(value)}
        placeholder="Search driver by name, email, ID, or phone number"
        isLoading={isLoading}
      />
      <div className="flex flex-col h-full">
        <DataTable
          total={data?.total || 0}
          resourceName="drivers"
          offset={offset}
          limit={limit}
          columns={DriverColumns}
          data={data!.drivers || []}
          currentPage={currentPage}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={(data?.total || 0) > offset + limit}
          hasPreviousPage={offset > 0}
          onPageChange={(change) => {
            params.set('offset', (offset + change * limit).toString());
            setParams(params);
          }}
          pageCount={Math.ceil((data?.total || 0) / limit)}
          onRowClick={(row) => {
            navigate(`/drivers/${row._id}`);
          }}
        />
      </div>
      <DriverSheet isOpen={isDriverSheetOpen} onClose={() => setIsDriverSheetOpen(false)} />
    </MainLayout>
  );
};
